// src/components/CourseRecommendation/CourseRecommendation.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styles from './CourseRecommendation.module.scss';

/**
 * Renders a course recommendation card.
 * Supports 'compact' mode (for tablet/mobile) showing only image + title.
 */
export default function CourseRecommendation({
  data,
  compact = false,
  host,
  className
}) {
  if (!data) return null;

  /**
   * Build a proper URL:
   * 1. If data.link is already a full URL (starts with http), use it.
   * 2. Otherwise, if we received a `host` prop, prepend it to the path.
   * 3. Fallback to data.link on its own.
   */
  const href = data.link
    ? data.link.startsWith('http')
      ? data.link
      : host
        ? `${host}${data.link}`
        : data.link
    : null;

  // --- COMPACT CARD: only image + title (for tablet/mobile) ---
  if (compact) {
    const Card = (
      <div className={`${styles.compactCard} ${className || ''}`}>
        <img
          src={data.image || '/assets/courserec.png'}
          alt={data.title}
        />
        <div className={styles.titleBar}>
          <div className={styles.titleText}>{data.title}</div>
        </div>
      </div>
    );
  
    return href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.compactLinkWrapper} ${className || ''}`}
      >
        {Card}
      </a>
    ) : (
      Card
    );
  }
  

  // --- FULL CARD: image, title, description, optional button (desktop) ---
  return (
    <div className={styles.courseRecommendation}>
      <div className={styles.leftColumn}>
        <img
          src={data.image || '/assets/courserec.png'}
          alt={data.title}
          className={styles.courseImage}
        />
        <h4 className={styles.title}>{data.title}</h4>
      </div>
      <div className={styles.rightColumn}>
        <p className={styles.description}>{data.intro}</p>
        {href && (
          // full‑card “Visit course page” button also uses href
          <a href={href} target="_blank" rel="noopener noreferrer">
            <button className={styles.button}>Gå till kurssidan</button>
          </a>
        )}
      </div>
    </div>
  );
}

CourseRecommendation.propTypes = {
  data: PropTypes.shape({
    courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    title: PropTypes.string.isRequired,
    intro: PropTypes.string,
    link: PropTypes.string,    // originally path or full URL
  }).isRequired,
  compact: PropTypes.bool,
  host:    PropTypes.string,   // e.g. "https://example.com"
  className: PropTypes.string,
};

CourseRecommendation.defaultProps = {
  compact: false,
  host:    null,
  className: '',
};
